import { CONFLICT_ERROR_CODE } from 'config';
import { i18n } from 'i18n';

const STOPE_UPDATE_HAS_POURS_ERROR = 'You cannot update a stope which has pours planned.';

interface ApiErrorLike {
  status?: number;
  message?: string;
  response?: string;
  result?: {
    title?: string;
    detail?: string;
    errors?: Record<string, string[]>;
  };
}

export interface UserFacingApiErrorMessage {
  title: string;
  details?: string[];
}

export const getStopeUpdateBlockedByExistingPoursMessage = (): UserFacingApiErrorMessage => ({
  title: i18n._('Stope details cannot be changed'),
  details: [
    i18n._('This stope already has one or more existing pours.'),
    i18n._('You can still update name, level, area, dates, and preset group, but volume and pipe details must stay unchanged.'),
  ],
});

export const getConcurrentSaveConflictMessage = (): UserFacingApiErrorMessage => ({
  title: i18n._('Another save changed this model first'),
  details: [i18n._('Your change was not saved. Reload the page and try again.')],
});

export const formatUserFacingApiErrorMessage = (errorMessage: UserFacingApiErrorMessage) => [
  errorMessage.title,
  ...(errorMessage.details ?? []),
].join(' ');

export const isStopeUpdateBlockedByExistingPoursError = (error: unknown) => {
  const apiError = error as ApiErrorLike | undefined;
  const validationMessages = apiError?.result?.errors
    ? Object.values(apiError.result.errors).flat()
    : [];
  const messages = [
    apiError?.message,
    apiError?.response,
    apiError?.result?.title,
    apiError?.result?.detail,
    ...validationMessages,
    // Guard on the actual type, not just truthiness: a non-string field (e.g. an object) would otherwise
    // slip through and crash the `.includes` check below.
  ].filter((message): message is string => typeof message === 'string');

  return messages.some((message) => message.includes(STOPE_UPDATE_HAS_POURS_ERROR));
};

export const getKnownApiErrorMessage = (error: unknown): UserFacingApiErrorMessage | undefined => {
  if (isStopeUpdateBlockedByExistingPoursError(error)) {
    return getStopeUpdateBlockedByExistingPoursMessage();
  }

  if ((error as ApiErrorLike | undefined)?.status === CONFLICT_ERROR_CODE) {
    return getConcurrentSaveConflictMessage();
  }

  return undefined;
};
